/* eslint-disable no-console */
import mqtt from 'mqtt';
import { eventChannel } from '@redux-saga/core';
import { put, call, take } from '@redux-saga/core/effects';
import { reportMessage } from 'lib/error';
import config from 'config';
import authToken from 'models/authToken';

let client;
let channel;
const topics = new Set();

function subscribe({ topic }) {
    return new Promise((resolve, reject) => {
        if (client.disconnecting || client.disconnected) {
            reject(new Error('Subscribe aborted - client is dissconnected'));
        }

        client.subscribe(topic, (error) => {
            if (error) {
                reportMessage({ message: `MQTT subscribe error - ${topic}`, error });
                return reject(error);
            }
            console.debug(`MQTT: subscribe to topic: ${topic}`);
            return resolve();
        });
    });
}

export function unsubscribe({ topic }) {
    return new Promise((resolve, reject) => {
        if (client.disconnecting || client.disconnected) {
            reject(new Error('Unsubscribe aborted - client is dissconnected'));
        }

        client.unsubscribe(topic, {}, (error) => {
            if (error) {
                reportMessage({
                    message: `MQTT unsubscribe error - ${topic}`,
                    error,
                });
                return reject(error);
            }
            console.debug(`MQTT: unsubscribe to topic: ${topic}`);
            topics.delete(topic);
            return resolve();
        });
    });
}

function createMqttChannel() {
    return eventChannel((emitter) => {
        const listener = (incomingTopic, message) => {
            console.debug({ data: JSON.parse(message.toString()), incomingTopic });
            emitter({ data: JSON.parse(message.toString()), incomingTopic });
        };

        client.on('message', listener);

        return () => {
            channel.close();
            channel = null;
            client.off('message', listener);
        };
    });
}

function transformWsUrl(url) {
    const { raw, exp } = authToken.get();
    if (exp * 1000 < Date.now()) {
        client.end();
    }
    const wsUrl = new URL(url);
    wsUrl.searchParams.set('token', raw);

    return wsUrl.toString();
}

function connectToMqtt() {
    return new Promise((resolve, reject) => {
        if (client) {
            resolve();
            return;
        }
        client = mqtt.connect(config.mqtt.url, {
            ...config.mqtt.options,
            transformWsUrl,
        });
        client.on('connect', () => console.debug(`MQTT: connected`));
        client.on('reconnect', () => console.debug('MQTT: reconnecting'));
        client.on('close', () => console.debug('MQTT: connection closed'));
        client.on('offline', () => console.debug('MQTT: went offline'));
        client.on('error', (error) => console.error(error, 'MQTT: error'));

        client.once('connect', () => resolve());
        client.once('error', (error) => {
            reportMessage({ message: `MQTT error - ${error?.message}`, error });
            return reject(error);
        });
    });
}

export function* watchForMqttEvents({ topic }) {
    try {
        if (authToken.isExpired()) {
            return;
        }

        if (topics.has(topic)) return;
        topics.add(topic);

        yield call(connectToMqtt);
        yield call(subscribe, { topic });

        if (channel) return;
        channel = yield call(createMqttChannel);

        while (true) {
            const { incomingTopic, data } = yield take(channel);
            yield put({ type: `MQTT/${incomingTopic}`, data });
        }
    } catch (error) {
        throw new Error(error);
    }
}
